import { useT } from "../../i18n/index.js";
import { formatMoney } from "../../lib/format-fr.js";

/**
 * Gráfico SVG inline do seuil de rentabilité.
 *
 * Eixo X: CA (de 0 até max(CA, seuil) × 1.2).
 * Curva 1: custos totais = CF + (1 − margeCv) × CA. Reta crescente.
 * Curva 2: receita = CA (diagonal y=x).
 * Ponto de equilíbrio: x = seuil_economique, y = seuil_economique.
 * Marcador: CA realizado atual (linha vertical pontilhada).
 *
 * Retorna `null` quando inputs insuficientes (taux≤0 ou seuil≤0).
 */
export function BreakevenChart({
  caHt,
  seuilEconomique,
  chargesFixes,
  tauxMargeCv,
}: {
  caHt: number | null;
  seuilEconomique: number | null;
  chargesFixes: number | null;
  tauxMargeCv: number | null;
}) {
  // Antes do early-return abaixo: hook não pode ficar após condicional.
  const t = useT();
  if (
    seuilEconomique === null ||
    seuilEconomique <= 0 ||
    chargesFixes === null ||
    tauxMargeCv === null ||
    tauxMargeCv <= 0
  ) {
    return (
      <div className="p-6 text-center text-neutral-500 text-sm border border-dashed border-neutral-800 rounded-lg">
        Gráfico indisponível — taux de marge sur coûts variables ≤ 0 ou exercício ainda sem dados de
        seuil.
      </div>
    );
  }

  // Domínio X: vai de 0 até max(ca, seuil) × 1.3, com no mínimo seuil × 1.5
  const xMax = Math.max((caHt ?? 0) * 1.3, seuilEconomique * 1.5, 1);
  // Coeficiente de custos variáveis sobre CA = 1 − tauxMargeCv (em razão)
  const taux = tauxMargeCv > 1 ? tauxMargeCv / 100 : tauxMargeCv;
  const costSlope = 1 - taux; // ex: taux=0.0849 → costSlope=0.9151
  // y máximo: max(receita em xMax, custos em xMax)
  const yMaxAtRight = Math.max(xMax, chargesFixes + costSlope * xMax);
  const yMax = yMaxAtRight * 1.05;

  const W = 720;
  const H = 360;
  const padL = 70;
  const padR = 20;
  const padT = 20;
  const padB = 40;
  const plotW = W - padL - padR;
  const plotH = H - padT - padB;

  const x = (val: number) => padL + (val / xMax) * plotW;
  const y = (val: number) => padT + plotH - (val / yMax) * plotH;

  // Receita: y=x; vai de (0,0) até (xMax,xMax)
  const revenueLine = `M ${x(0)} ${y(0)} L ${x(xMax)} ${y(xMax)}`;
  // Custo total: vai de (0, CF) até (xMax, CF + costSlope × xMax)
  const costLine = `M ${x(0)} ${y(chargesFixes)} L ${x(xMax)} ${y(chargesFixes + costSlope * xMax)}`;
  // Área de lucro (entre receita e custo, à direita do seuil)
  const profitArea = `M ${x(seuilEconomique)} ${y(seuilEconomique)} L ${x(xMax)} ${y(xMax)} L ${x(xMax)} ${y(chargesFixes + costSlope * xMax)} Z`;

  // Ticks: 0, 25%, 50%, 75%, 100% do xMax
  const xTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => p * xMax);
  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((p) => p * yMax);

  return (
    <div className="w-full overflow-x-auto">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="w-full h-auto"
        role="img"
        aria-label={t("breakevenChart.ariaLabel")}
      >
        {/* Grid */}
        {xTicks.map((t, i) => (
          <line
            key={`vx-${i}`}
            x1={x(t)}
            x2={x(t)}
            y1={padT}
            y2={padT + plotH}
            stroke="rgb(38 38 38)"
            strokeWidth={1}
          />
        ))}
        {yTicks.map((t, i) => (
          <line
            key={`vy-${i}`}
            x1={padL}
            x2={padL + plotW}
            y1={y(t)}
            y2={y(t)}
            stroke="rgb(38 38 38)"
            strokeWidth={1}
          />
        ))}

        {/* Área de lucro */}
        <path d={profitArea} fill="rgb(16 185 129 / 0.10)" />

        {/* Eixos */}
        <line
          x1={padL}
          x2={padL + plotW}
          y1={padT + plotH}
          y2={padT + plotH}
          stroke="rgb(82 82 82)"
        />
        <line x1={padL} x2={padL} y1={padT} y2={padT + plotH} stroke="rgb(82 82 82)" />

        {/* Curva: custos */}
        <path d={costLine} fill="none" stroke="rgb(248 113 113)" strokeWidth={2} />
        {/* Curva: receita */}
        <path d={revenueLine} fill="none" stroke="rgb(96 165 250)" strokeWidth={2} />

        {/* Ponto de equilíbrio */}
        <circle
          cx={x(seuilEconomique)}
          cy={y(seuilEconomique)}
          r={5}
          fill="rgb(251 191 36)"
          stroke="rgb(15 23 42)"
          strokeWidth={2}
        />
        <line
          x1={x(seuilEconomique)}
          x2={x(seuilEconomique)}
          y1={y(seuilEconomique)}
          y2={padT + plotH}
          stroke="rgb(251 191 36)"
          strokeWidth={1}
          strokeDasharray="3 3"
        />

        {/* Marcador de CA atual */}
        {caHt !== null && caHt > 0 && (
          <>
            <line
              x1={x(caHt)}
              x2={x(caHt)}
              y1={padT}
              y2={padT + plotH}
              stroke="rgb(16 185 129)"
              strokeWidth={1.5}
              strokeDasharray="5 3"
            />
            <text
              x={x(caHt) + 4}
              y={padT + 14}
              fill="rgb(16 185 129)"
              fontSize={11}
              fontFamily="ui-monospace, monospace"
            >
              CA: {formatMoney(caHt)}
            </text>
          </>
        )}

        {/* Labels eixos */}
        {xTicks.map((t, i) => (
          <text
            key={`xt-${i}`}
            x={x(t)}
            y={padT + plotH + 16}
            fontSize={10}
            fill="rgb(115 115 115)"
            textAnchor="middle"
            fontFamily="ui-monospace, monospace"
          >
            {formatMoney(t)}
          </text>
        ))}
        {yTicks.map((t, i) => (
          <text
            key={`yt-${i}`}
            x={padL - 6}
            y={y(t) + 3}
            fontSize={10}
            fill="rgb(115 115 115)"
            textAnchor="end"
            fontFamily="ui-monospace, monospace"
          >
            {formatMoney(t)}
          </text>
        ))}

        {/* Label do ponto de equilíbrio */}
        <text
          x={x(seuilEconomique) + 8}
          y={y(seuilEconomique) - 8}
          fill="rgb(251 191 36)"
          fontSize={11}
          fontFamily="ui-monospace, monospace"
        >
          Seuil: {formatMoney(seuilEconomique)}
        </text>

        {/* Legenda */}
        <g transform={`translate(${padL + 12}, ${padT + 12})`}>
          <rect width={150} height={50} fill="rgb(23 23 23 / 0.8)" rx={4} />
          <line x1={8} y1={14} x2={24} y2={14} stroke="rgb(96 165 250)" strokeWidth={2} />
          <text x={28} y={17} fontSize={10} fill="rgb(212 212 212)">
            Receita (CA HT)
          </text>
          <line x1={8} y1={32} x2={24} y2={32} stroke="rgb(248 113 113)" strokeWidth={2} />
          <text x={28} y={35} fontSize={10} fill="rgb(212 212 212)">
            Custos totais (CF + CV)
          </text>
        </g>
      </svg>
    </div>
  );
}
